import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Páginas
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Feed from './pages/Feed'; // Importando a nova página de Feed
import Ranking from './pages/Ranking';
import Perfil from './pages/Perfil';
import Pendente from './pages/Pendente';
import Admin from './pages/Admin';
import PostDetail from './pages/PostDetail';
import VideoDetail from './pages/VideoDetail';
import StoryViewer from './pages/StoryViewer';
import BestClips from './pages/BestClips';
import ShameWall from './pages/ShameWall';
import Termos from './pages/Termos';
import NotFound from './pages/NotFound';

// Componente de rota protegida
const ProtectedRoute = ({ children, requireAdmin, requireApproved }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Mostra loading enquanto verifica autenticação ou processa token da URL
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Se não está autenticado, redireciona para login
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Se precisa ser admin e não é, redireciona para feed
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/feed" replace />;
  }

  // Se precisa ser aprovado e não é, redireciona para pendente
  if (requireApproved && user.status !== 'approved') {
    return <Navigate to="/pendente" replace />;
  }

  // Se está pendente e tenta acessar feed ou outras rotas aprovadas, redireciona para pendente
  if (user.status === 'pending' && (location.pathname === '/feed' || location.pathname === '/ranking' || location.pathname === '/perfil')) {
    return <Navigate to="/pendente" replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Rotas públicas */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/termos" element={<Termos />} />

        {/* Rota para usuários pendentes */}
        <Route 
          path="/pendente" 
          element={
            <ProtectedRoute>
              <Pendente />
            </ProtectedRoute>
          } 
        />

        {/* Redirecionar /dashboard para /feed */}
        <Route 
          path="/dashboard" 
          element={
            <Navigate to="/feed" replace />
          } 
        />

        {/* Nova rota para o feed */}
        <Route 
          path="/feed" 
          element={
            <ProtectedRoute requireApproved>
              <Feed />
            </ProtectedRoute>
          } 
        />

        {/* Outras rotas para usuários aprovados */}
        <Route 
          path="/ranking" 
          element={
            <ProtectedRoute requireApproved>
              <Ranking />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/perfil" 
          element={
            <ProtectedRoute requireApproved>
              <Perfil />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/perfil/:userId" 
          element={
            <ProtectedRoute requireApproved>
              <Perfil />
            </ProtectedRoute>
          } 
        />

        {/* Rota para detalhes do post */}
        <Route 
          path="/post/:postId" 
          element={
            <ProtectedRoute requireApproved>
              <PostDetail />
            </ProtectedRoute>
          } 
        />

        {/* Rota para detalhes do vídeo */}
        <Route 
          path="/video/:videoType/:videoId" 
          element={
            <ProtectedRoute requireApproved>
              <VideoDetail />
            </ProtectedRoute>
          } 
        />

        {/* Rota para visualizar story */}
        <Route 
          path="/story/:storyId" 
          element={
            <ProtectedRoute requireApproved>
              <StoryViewer />
            </ProtectedRoute>
          } 
        />

        {/* Rotas para listagem de vídeos */}
        <Route 
          path="/best-clips" 
          element={
            <ProtectedRoute requireApproved>
              <BestClips />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/shame-wall" 
          element={
            <ProtectedRoute requireApproved>
              <ShameWall />
            </ProtectedRoute>
          } 
        />

        {/* Rota para administradores */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute requireAdmin>
              <Admin />
            </ProtectedRoute>
          } 
        />

        {/* Rota 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;